import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';

const GOLD = '#c9a961';
const GOLD_LIGHT = '#e8d5a3';
const CARD_BG = '#0d2818';
const TEXT_MUTED = '#b8a68f';
const PANEL_BG = 'rgba(201,169,97,0.06)';
const PANEL_BORDER = 'rgba(201,169,97,0.35)';

// ── Icons ────────────────────────────────────────────────────────────
// Inline SVG, gold-stroked, matching the infographic's icon style. No
// external icon library in this project, so these are hand-built to
// keep the bundle dependency-free.

const iconProps = { viewBox: '0 0 48 48', fill: 'none', stroke: GOLD, strokeWidth: 2.2, strokeLinecap: 'round', strokeLinejoin: 'round' };

function IconPeople({ size = 40 }) {
  return (
    <svg width={size} height={size} {...iconProps}>
      <circle cx="24" cy="14" r="6" />
      <circle cx="9" cy="20" r="4.5" />
      <circle cx="39" cy="20" r="4.5" />
      <path d="M14 38c0-6.5 4.5-11 10-11s10 4.5 10 11" />
      <path d="M4 34c0-4.5 2.5-8 6-9" />
      <path d="M38 25c3.5 1 6 4.5 6 9" />
    </svg>
  );
}

function IconCoins({ size = 40 }) {
  return (
    <svg width={size} height={size} {...iconProps}>
      <ellipse cx="24" cy="14" rx="10" ry="4" />
      <path d="M14 14v6c0 2.2 4.5 4 10 4s10-1.8 10-4v-6" />
      <path d="M14 20v6c0 2.2 4.5 4 10 4s10-1.8 10-4v-6" />
      <path d="M14 26v6c0 2.2 4.5 4 10 4s10-1.8 10-4v-6" />
    </svg>
  );
}

function IconNetwork({ size = 40 }) {
  return (
    <svg width={size} height={size} {...iconProps}>
      <circle cx="24" cy="9" r="5" />
      <circle cx="9" cy="34" r="5" />
      <circle cx="39" cy="34" r="5" />
      <path d="M24 14v10" />
      <path d="M24 24l-11 6" />
      <path d="M24 24l11 6" />
    </svg>
  );
}

function IconTrendUp({ size = 40 }) {
  return (
    <svg width={size} height={size} {...iconProps}>
      <path d="M6 36h36" />
      <path d="M9 30l9-10 7 6 12-15" />
      <path d="M29 11h8v8" />
    </svg>
  );
}

function IconGlobe({ size = 40 }) {
  return (
    <svg width={size} height={size} {...iconProps}>
      <circle cx="24" cy="24" r="17" />
      <ellipse cx="24" cy="24" rx="7" ry="17" />
      <path d="M7 24h34" />
      <path d="M10 14h28" />
      <path d="M10 34h28" />
    </svg>
  );
}

function IconCrown({ size = 40 }) {
  return (
    <svg width={size} height={size} {...iconProps}>
      <path d="M8 34h32l-3-16-9 8-4-14-4 14-9-8z" strokeLinejoin="round" />
      <path d="M8 34h32" />
      <circle cx="8" cy="14" r="2" fill={GOLD} stroke="none" />
      <circle cx="24" cy="10" r="2" fill={GOLD} stroke="none" />
      <circle cx="40" cy="14" r="2" fill={GOLD} stroke="none" />
    </svg>
  );
}

function IconWallet({ size = 40 }) {
  return (
    <svg width={size} height={size} {...iconProps}>
      <rect x="6" y="12" width="36" height="26" rx="4" />
      <path d="M6 20h36" />
      <circle cx="33" cy="29" r="2.2" fill={GOLD} stroke="none" />
      <path d="M14 12V9a3 3 0 013-3h10a3 3 0 013 3v3" />
    </svg>
  );
}

function IconClover({ size = 32 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none" stroke="#fff" strokeWidth="1.5">
      <path d="M24 24c0-6-4-10-8-10s-6 3-6 6 3 6 6 6h8z" fill="#fff" opacity="0.9" />
      <path d="M24 24c0-6 4-10 8-10s6 3 6 6-3 6-6 6h-8z" fill="#fff" opacity="0.9" />
      <path d="M24 24c-6 0-10 4-10 8s3 6 6 6 6-3 6-6v-8z" fill="#fff" opacity="0.9" />
      <path d="M24 24c6 0 10 4 10 8s-3 6-6 6-6-3-6-6v-8z" fill="#fff" opacity="0.9" />
      <path d="M24 24v14" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function IconShield({ size = 32 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M24 6l14 5v11c0 10-6 17-14 20-8-3-14-10-14-20V11z" fill="none" />
      <path d="M17 24l5 5 9-11" />
    </svg>
  );
}

function IconLotus({ size = 32 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="#fff" opacity="0.95">
      <ellipse cx="24" cy="30" rx="4" ry="10" />
      <ellipse cx="24" cy="30" rx="4" ry="10" transform="rotate(45 24 30)" />
      <ellipse cx="24" cy="30" rx="4" ry="10" transform="rotate(-45 24 30)" />
      <ellipse cx="24" cy="30" rx="4" ry="10" transform="rotate(90 24 30)" />
      <ellipse cx="24" cy="30" rx="4" ry="10" transform="rotate(22 24 30)" />
      <ellipse cx="24" cy="30" rx="4" ry="10" transform="rotate(-22 24 30)" />
      <circle cx="24" cy="30" r="3" />
    </svg>
  );
}

const PACKAGE_BADGES = {
  package_auspicious: { Icon: IconClover, ring: '#3a7d5c', bg: 'radial-gradient(circle at 35% 30%, #4d9970, #235f42)' },
  package_good_health: { Icon: IconShield, ring: '#3f7fb0', bg: 'radial-gradient(circle at 35% 30%, #5695c9, #23577c)' },
  package_fulfillment: { Icon: IconLotus, ring: '#8f6bb8', bg: 'radial-gradient(circle at 35% 30%, #a685c9, #5c3f80)' },
  package_blessing: { Icon: null, ring: '#c94961', bg: 'radial-gradient(circle at 35% 30%, #e0576b, #8c1f2e)' },
};

function PackageBadge({ pkgKey, size = 56 }) {
  const cfg = PACKAGE_BADGES[pkgKey];
  const { Icon } = cfg;
  return (
    <div
      className="rounded-full flex items-center justify-center flex-shrink-0"
      style={{ width: size, height: size, background: cfg.bg, border: `2px solid ${cfg.ring}`, boxShadow: `0 0 0 3px rgba(0,0,0,0.25), 0 2px 8px rgba(0,0,0,0.4)` }}
    >
      {Icon ? <Icon size={size * 0.55} /> : <span className="text-white font-bold" style={{ fontSize: size * 0.4 }}>福</span>}
    </div>
  );
}

// ── Layout pieces ────────────────────────────────────────────────────

function SectionCard({ number, title, subtitle, children, accent }) {
  return (
    <section
      className="rounded-xl border-2 p-5 md:p-8 relative overflow-hidden"
      style={{ backgroundColor: CARD_BG, borderColor: GOLD, boxShadow: '0 4px 20px rgba(0,0,0,0.35)' }}
    >
      <div
        className="absolute top-0 left-0 right-0 h-1"
        style={{ background: `linear-gradient(90deg, transparent, ${accent || GOLD}, transparent)` }}
      />
      <div className="flex items-center gap-3 mb-1 flex-wrap">
        <div
          className="w-12 h-12 flex-shrink-0 rounded-full flex items-center justify-center font-bold text-black text-2xl"
          style={{ background: `linear-gradient(145deg, ${GOLD_LIGHT}, ${GOLD})`, boxShadow: '0 2px 6px rgba(0,0,0,0.4)' }}
        >
          {number}
        </div>
        <h2 className="text-2xl md:text-4xl font-bold" style={{ color: GOLD }}>{title}</h2>
        {subtitle && <span className="text-base md:text-lg" style={{ color: TEXT_MUTED }}>{subtitle}</span>}
      </div>
      <div className="mt-4">{children}</div>
    </section>
  );
}

function TeamBonusCoins({ t }) {
  const coins = [
    { pct: '5%', labelKey: 'team_bonus_level_1' },
    { pct: '8%', labelKey: 'team_bonus_level_2' },
    { pct: '10%', labelKey: 'team_bonus_level_3' },
    { pct: '13%', labelKey: 'team_bonus_level_4' },
    { pct: '16%', labelKey: 'team_bonus_level_5' },
  ];
  return (
    <div>
      <div className="flex flex-wrap justify-center gap-4 md:gap-6 mb-4">
        {coins.map((coin) => (
          <div key={coin.labelKey} className="flex flex-col items-center gap-2">
            <div
              className="w-20 h-20 md:w-24 md:h-24 rounded-full flex items-center justify-center font-bold text-lg md:text-xl flex-shrink-0"
              style={{
                background: `radial-gradient(circle at 35% 30%, ${GOLD_LIGHT}, ${GOLD} 55%, #8a6f3a)`,
                color: '#0d2818',
                boxShadow: '0 3px 10px rgba(0,0,0,0.4), inset 0 2px 4px rgba(255,255,255,0.4)',
                border: `2px solid ${GOLD_LIGHT}`,
              }}
            >
              {coin.pct}
            </div>
            <div className="text-sm md:text-base font-medium" style={{ color: TEXT_MUTED }}>{t(`marketingPlan.${coin.labelKey}`)}</div>
          </div>
        ))}
      </div>
      <div className="flex justify-center">
        <div
          className="px-6 py-3 rounded-lg text-center"
          style={{ background: `linear-gradient(135deg, ${GOLD_LIGHT}, ${GOLD} 60%, #a4823f)` }}
        >
          <p className="text-sm md:text-base font-semibold" style={{ color: '#0d2818' }}>{t('marketingPlan.team_bonus_same_rank_note')}</p>
        </div>
      </div>
    </div>
  );
}

function IncomeStreamBox({ Icon, title, rate, note }) {
  return (
    <div
      className="rounded-xl border p-5 text-center flex flex-col items-center transition-transform hover:scale-[1.03]"
      style={{ borderColor: PANEL_BORDER, background: `linear-gradient(160deg, ${PANEL_BG}, rgba(0,0,0,0.15))` }}
    >
      <Icon size={52} />
      <div className="text-base mt-3 mb-1" style={{ color: TEXT_MUTED }}>{title}</div>
      <div className="text-2xl md:text-3xl font-bold" style={{ color: GOLD }}>{rate}</div>
      <div className="text-sm mt-1" style={{ color: TEXT_MUTED }}>{note}</div>
    </div>
  );
}

function TeamIncentiveChart({ t }) {
  const bars = [
    { label: t('marketingPlan.star_1'), stars: 1, pct: 20 },
    { label: t('marketingPlan.star_2'), stars: 2, pct: 30 },
    { label: t('marketingPlan.star_3'), stars: 3, pct: 40 },
    { label: t('marketingPlan.star_4'), stars: 4, pct: 50 },
    { label: t('marketingPlan.star_5'), stars: 5, pct: 60 },
  ];
  const maxPct = 60;
  return (
    <div className="flex items-end justify-between gap-2 md:gap-4 h-64 md:h-72 px-2">
      {bars.map((bar) => (
        <div key={bar.label} className="flex flex-col items-center flex-1 h-full justify-end">
          <div className="text-lg md:text-xl font-bold mb-1" style={{ color: GOLD }}>{bar.pct}%</div>
          <div
            className="w-full rounded-t-md"
            style={{
              height: `${(bar.pct / maxPct) * 100}%`,
              background: `linear-gradient(180deg, ${GOLD_LIGHT} 0%, ${GOLD} 60%, #8a6f3a 100%)`,
              minHeight: '8px',
              boxShadow: '0 -2px 8px rgba(201,169,97,0.35)',
            }}
          />
          <div className="text-sm mt-1" style={{ color: GOLD }}>
            {'★'.repeat(bar.stars)}
          </div>
          <div className="text-sm md:text-base whitespace-nowrap font-medium" style={{ color: TEXT_MUTED }}>{bar.label}</div>
        </div>
      ))}
    </div>
  );
}

function MarketingPlanContent({ t }) {
  const packages = [
    { key: 'package_auspicious', price: '550', cash: '250', product: '250', total: '500' },
    { key: 'package_good_health', price: '880', cash: '400', product: '400', total: '800' },
    { key: 'package_fulfillment', price: '4,500', cash: '2,300', product: '2,300', total: '4,600' },
    { key: 'package_blessing', price: '38,000', cash: '15,000', product: '15,000', shares: '10,000', total: '40,000' },
  ];

  const streams = [
    { Icon: IconPeople, title: t('marketingPlan.stream_direct_referral'), rate: t('marketingPlan.stream_direct_referral_rate'), note: t('marketingPlan.stream_direct_referral_note') },
    { Icon: IconCoins, title: t('marketingPlan.stream_passive'), rate: t('marketingPlan.stream_passive_rate'), note: t('marketingPlan.stream_passive_note') },
    { Icon: IconNetwork, title: t('marketingPlan.stream_team_bonus'), rate: t('marketingPlan.stream_team_bonus_rate'), note: t('marketingPlan.stream_team_bonus_note') },
    { Icon: IconTrendUp, title: t('marketingPlan.stream_team_incentive'), rate: t('marketingPlan.stream_team_incentive_rate'), note: t('marketingPlan.stream_team_incentive_note') },
    { Icon: IconGlobe, title: t('marketingPlan.stream_global_dividend'), rate: t('marketingPlan.stream_global_dividend_rate'), note: t('marketingPlan.stream_global_dividend_note') },
    { Icon: IconCrown, title: t('marketingPlan.stream_advancement'), rate: t('marketingPlan.stream_advancement_rate'), note: t('marketingPlan.stream_advancement_note') },
  ];

  const ranks = [
    { key: 'rank_1star', target: '$5,000', conditionKey: 'condition_1star' },
    { key: 'rank_2star', target: '$10,000', conditionKey: 'condition_2star' },
    { key: 'rank_3star', target: '$50,000', conditionKey: 'condition_3star' },
    { key: 'rank_4star', target: '$100,000', conditionKey: 'condition_4star' },
    { key: 'rank_5star', target: '$200,000', conditionKey: 'condition_5star' },
  ];

  const shareRows = [
    { price: '550', sharesKey: 'shares_550' },
    { price: '880', sharesKey: 'shares_880' },
    { price: '4,500', sharesKey: 'shares_4500' },
    { price: '38,000', sharesKey: 'shares_38000' },
  ];

  return (
    <div className="space-y-6">
      {/* Section 1: Entry Packages + Important Notes */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <SectionCard number={1} title={t('marketingPlan.section1_title')} subtitle={t('marketingPlan.section1_subtitle')}>
            <div className="overflow-x-auto">
              <table className="w-full text-base md:text-lg">
                <thead>
                  <tr className="border-b" style={{ borderColor: 'rgba(201,169,97,0.4)' }}>
                    <th className="text-left py-2 px-2" style={{ color: TEXT_MUTED }}>{t('marketingPlan.table_package_name')}</th>
                    <th className="text-left py-2 px-2" style={{ color: TEXT_MUTED }}>{t('marketingPlan.table_price')}</th>
                    <th className="text-left py-2 px-2" style={{ color: TEXT_MUTED }}>{t('marketingPlan.table_reward')}</th>
                  </tr>
                </thead>
                <tbody>
                  {packages.map((pkg) => (
                    <tr key={pkg.key} className="border-b" style={{ borderColor: 'rgba(201,169,97,0.15)' }}>
                      <td className="py-4 px-2">
                        <div className="flex items-center gap-3">
                          <PackageBadge pkgKey={pkg.key} size={64} />
                          <div>
                            <div className="font-semibold text-lg md:text-xl" style={{ color: GOLD }}>{t(`marketingPlan.${pkg.key}`)}</div>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-2">
                        <div className="font-bold text-lg md:text-xl" style={{ color: GOLD }}>US${pkg.price}</div>
                        <div className="text-sm" style={{ color: TEXT_MUTED }}>{t('marketingPlan.points_label')}</div>
                      </td>
                      <td className="py-4 px-2">
                        <div className="font-bold text-lg md:text-xl" style={{ color: GOLD }}>{t('marketingPlan.reward_total')} US${pkg.total}</div>
                        <div className="text-sm" style={{ color: TEXT_MUTED }}>
                          (US${pkg.cash} {t('marketingPlan.cash')} + US${pkg.product} {t('marketingPlan.product')}
                          {pkg.shares ? ` + US$${pkg.shares} ${t('marketingPlan.shares')}` : ''})
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </SectionCard>
        </div>

        <div
          className="rounded-xl border-2 p-6 relative overflow-hidden"
          style={{ backgroundColor: CARD_BG, borderColor: GOLD, boxShadow: '0 4px 20px rgba(0,0,0,0.35)' }}
        >
          <div className="absolute top-0 left-0 right-0 h-1" style={{ background: `linear-gradient(90deg, transparent, #d97706, transparent)` }} />
          <div className="flex items-center gap-2 mb-4">
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center font-bold text-black flex-shrink-0 text-lg"
              style={{ background: `linear-gradient(145deg, ${GOLD_LIGHT}, ${GOLD})` }}
            >
              !
            </div>
            <h3 className="text-xl md:text-2xl font-bold" style={{ color: GOLD }}>{t('marketingPlan.important_notes_title')}</h3>
          </div>
          <div className="space-y-3 text-base">
            <p style={{ color: TEXT_MUTED }}>{t('marketingPlan.cap_note1')}</p>
            <div className="border-t pt-3" style={{ borderColor: 'rgba(201,169,97,0.2)' }}>
              <p style={{ color: TEXT_MUTED }}>{t('marketingPlan.cap_note2')}</p>
              <p className="font-bold mt-1 text-xl" style={{ color: GOLD }}>{t('marketingPlan.cap_amount')}</p>
            </div>
            <div className="border-t pt-3" style={{ borderColor: 'rgba(201,169,97,0.2)' }}>
              <p style={{ color: TEXT_MUTED }}>{t('marketingPlan.cap_note3')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Section 2: Six Income Streams */}
      <SectionCard number={2} title={t('marketingPlan.section2_title')}>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {streams.map((s) => (
            <IncomeStreamBox key={s.title} Icon={s.Icon} title={s.title} rate={s.rate} note={s.note} />
          ))}
        </div>
      </SectionCard>

      {/* Section 3: Team Incentive Chart */}
      <SectionCard number={3} title={t('marketingPlan.section3_title')} subtitle={t('marketingPlan.section3_subtitle')}>
        <TeamIncentiveChart t={t} />
      </SectionCard>

      {/* Section 4: Advancement Requirements */}
      <SectionCard number={4} title={t('marketingPlan.section4_title')} subtitle={t('marketingPlan.section4_subtitle')}>
        <div className="overflow-x-auto">
          <table className="w-full text-base md:text-lg">
            <thead>
              <tr className="border-b" style={{ borderColor: 'rgba(201,169,97,0.4)' }}>
                <th className="text-left py-2 px-2" style={{ color: TEXT_MUTED }}>{t('marketingPlan.table_rank')}</th>
                {ranks.map((r, idx) => (
                  <th key={r.key} className="text-center py-2 px-2">
                    <div className="flex items-center justify-center gap-1">
                      {idx > 0 && <span style={{ color: GOLD }}>&rarr;</span>}
                      <span className="font-bold whitespace-nowrap" style={{ color: GOLD }}>{'★'.repeat(idx + 1)} {t(`marketingPlan.${r.key}`)}</span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr className="border-b" style={{ borderColor: 'rgba(201,169,97,0.15)' }}>
                <td className="py-4 px-2" style={{ color: TEXT_MUTED }}>{t('marketingPlan.table_sales_target')}</td>
                {ranks.map((r) => (
                  <td key={r.key} className="text-center py-4 px-2 font-bold text-lg md:text-xl" style={{ color: GOLD }}>{r.target}</td>
                ))}
              </tr>
              <tr>
                <td className="py-4 px-2 align-top" style={{ color: TEXT_MUTED }}>{t('marketingPlan.table_advancement_condition')}</td>
                {ranks.map((r) => (
                  <td key={r.key} className="text-center py-4 px-2 text-sm align-top" style={{ color: TEXT_MUTED }}>{t(`marketingPlan.${r.conditionKey}`)}</td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </SectionCard>

      {/* Sections 5 & 6 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <SectionCard number={5} title={t('marketingPlan.section5_title')}>
          <div className="flex gap-4">
            <div className="flex-shrink-0 pt-1">
              <IconWallet size={56} />
            </div>
            <div className="flex-1">
              <ul className="space-y-2 text-base mb-4" style={{ color: TEXT_MUTED }}>
                <li>&bull; {t('marketingPlan.passive_point1')}</li>
                <li>&bull; {t('marketingPlan.passive_point2')}</li>
              </ul>
              <p className="text-base font-medium mb-2" style={{ color: GOLD }}>{t('marketingPlan.passive_point3')}</p>
              <div className="grid grid-cols-4 gap-2">
                {shareRows.map((row) => (
                  <div
                    key={row.price}
                    className="rounded-lg border text-center py-3"
                    style={{ borderColor: PANEL_BORDER, background: PANEL_BG }}
                  >
                    <div className="text-sm" style={{ color: TEXT_MUTED }}>{row.price}</div>
                    <div className="font-bold text-base md:text-lg" style={{ color: GOLD }}>{t(`marketingPlan.${row.sharesKey}`)}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </SectionCard>

        <SectionCard number={6} title={t('marketingPlan.section6_title')}>
          <div className="flex gap-4 items-start">
            <div className="flex-shrink-0 pt-1">
              <IconGlobe size={56} />
            </div>
            <div className="flex-1">
              <p className="text-base mb-4" style={{ color: TEXT_MUTED }}>{t('marketingPlan.global_dividend_point1')}</p>
              <p className="text-xl md:text-2xl font-bold" style={{ color: GOLD }}>{t('marketingPlan.global_dividend_tagline')}</p>
            </div>
          </div>
        </SectionCard>
      </div>

      {/* Section 7: Team Bonus */}
      <SectionCard number={7} title={t('marketingPlan.section7_title')}>
        <TeamBonusCoins t={t} />
      </SectionCard>

      {/* Footer tagline - gold ribbon banner */}
      <div className="flex justify-center py-2">
        <div
          className="px-8 py-5 rounded-lg text-center"
          style={{ background: `linear-gradient(135deg, ${GOLD_LIGHT}, ${GOLD} 60%, #a4823f)`, boxShadow: '0 4px 16px rgba(0,0,0,0.4)' }}
        >
          <p className="text-xl md:text-2xl font-bold" style={{ color: '#0d2818' }}>{t('marketingPlan.footer_tagline')}</p>
        </div>
      </div>
    </div>
  );
}


export default function MarketingPlan() {
  const { user, loading: authLoading } = useAuth();
  const { t, i18n } = useTranslation('app');
  const navigate = useNavigate();
  const [packageStatus, setPackageStatus] = React.useState(null);
  const [buyRates, setBuyRates] = React.useState(null);

  useEffect(() => {
    // Public endpoint, no auth required -- same numbers every visitor sees.
    const fetchBuyRates = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/public/credit-buy-rates`);
        if (response.ok) {
          const data = await response.json();
          setBuyRates(data.buy_rates);
        }
      } catch (err) {
        console.error('Failed to fetch credit buy rates:', err);
      }
    };
    fetchBuyRates();
  }, []);

  useEffect(() => {
    if (authLoading || !user) return;

    // Fetch package status (logged-in members only -- public visitors just
    // see the default "become a verified member" CTA)
    const fetchPackageStatus = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/user/package-status`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (response.ok) {
          const data = await response.json();
          setPackageStatus(data);
        }
      } catch (err) {
        console.error('Failed to fetch package status:', err);
      }
    };

    fetchPackageStatus();
  }, [authLoading, user]);

  if (authLoading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  const isEnglish = i18n.language === 'en';

  return (
    <div className="min-h-screen pt-20" style={{ backgroundColor: '#1a3a2a' }}>
      {/* Header - uses shared LanguageSwitcher in AppHeader */}
      <div
        className="sticky top-16 z-20 backdrop-blur-md border-b-2"
        style={{ borderColor: GOLD, background: 'linear-gradient(180deg, rgba(13,40,24,0.98), rgba(26,58,42,0.9))' }}
      >
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-2">
            <span style={{ color: GOLD }} className="text-3xl">&#10047;</span>
            <h1
              className="text-2xl md:text-4xl font-bold"
              style={{ color: GOLD, textShadow: '0 2px 8px rgba(201,169,97,0.3)' }}
            >
              {t('marketingPlan.title')}
            </h1>
          </div>
          <p className="text-base md:text-lg mt-1" style={{ color: TEXT_MUTED }}>
            {t('marketingPlan.subtitle')} &mdash; {t('marketingPlan.tagline')}
          </p>

          {/* Download buttons -- both languages, since a member may want
              to share either version with a prospect regardless of their
              own UI language setting. */}
          <div className="flex flex-wrap items-center gap-2 mt-3">
            <span className="text-sm mr-1" style={{ color: TEXT_MUTED }}>
              {isEnglish ? 'Download:' : '下载：'}
            </span>
            {[
              { href: '/marketing-plan/marketing_plan_en.pdf', label: isEnglish ? 'PDF (EN)' : 'PDF（英文）' },
              { href: '/marketing-plan/marketing_plan_cn.pdf', label: isEnglish ? 'PDF (CN)' : 'PDF（中文）' },
              { href: '/marketing-plan/marketing_plan_en.png', label: isEnglish ? 'Image (EN)' : '图片（英文）' },
              { href: '/marketing-plan/marketing_plan_cn.png', label: isEnglish ? 'Image (CN)' : '图片（中文）' },
            ].map((item) => (
              <a
                key={item.href}
                href={item.href}
                download
                className="px-3 py-2 rounded text-sm font-medium border hover:opacity-90 transition-opacity"
                style={{ borderColor: GOLD, color: GOLD, backgroundColor: 'rgba(201,169,97,0.08)' }}
              >
                &#8681; {item.label}
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* Verification CTA */}
      {!packageStatus?.verified && (
        <div className="max-w-7xl mx-auto px-4 py-6 mb-4">
          <div className="border rounded-lg p-4" style={{ backgroundColor: CARD_BG, borderColor: GOLD }}>
            <p style={{ color: TEXT_MUTED }} className="text-body-sm">
              {isEnglish ? 'Ready to become a verified member?' : '准备好成为认证会员了吗？'}
              <button
                onClick={() => navigate('/packages')}
                className="ml-2 font-medium hover:underline"
                style={{ color: GOLD }}
              >
                {isEnglish ? 'View Packages →' : '查看套餐 →'}
              </button>
            </p>
          </div>
        </div>
      )}

      {/* Credit Points Buy Rates */}
      {buyRates && Object.keys(buyRates).length > 0 && (
        <div className="max-w-7xl mx-auto px-4 mb-4">
          <div className="border rounded-lg p-4" style={{ backgroundColor: CARD_BG, borderColor: GOLD }}>
            <p className="text-body-sm font-medium mb-3" style={{ color: GOLD }}>
              {isEnglish ? 'Credit Points Buy Rates' : '认购积分购买汇率'}
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {Object.entries(buyRates).map(([currency, rate]) => (
                <div key={currency} className="p-3 border rounded" style={{ backgroundColor: '#152214', borderColor: '#3d5a4a' }}>
                  <p className="text-xs mb-1" style={{ color: TEXT_MUTED }}>{currency} / Credit</p>
                  <p className="font-medium" style={{ color: GOLD }}>{rate}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <MarketingPlanContent t={t} />
      </div>
    </div>
  );
}
